export type CurriculumTrack = 'Basic' | 'Advanced' | 'Standard'

export interface CurriculumEntry {
  level: string
  track: CurriculumTrack
  title: string
  subject: string
  intro: string[]
  learningObjectives: string[]
  project: {
    name: string
    description: string
  }
  closing: string[]
}

export const cohortCurriculumProgram = {
  program: 'RYD Learning Cohort Emails for Parents',
  levels: [
    {
      level: 'Level 1',
      track: 'Basic',
      title: 'Introduction to Block-Based Programming',
      subject: 'Welcome to Level 1 – Introduction to Block-Based Programming',
      intro: [
        'Dear Parent,',
        'We are excited to welcome your child to Level 1: Introduction to Block-Based Programming in our upcoming cohort.',
        'In this level, students will begin their coding journey by learning the fundamentals of computer science through fun and interactive block-based programming activities. They will explore important concepts such as inputs and outputs, sequencing, storage, sprites, events, and loops while developing problem-solving and logical thinking skills.',
        'Throughout the cohort, students will participate in hands-on activities designed to build confidence and creativity in coding.',
      ],
      learningObjectives: [
        'Introduction to Computer Science',
        'Inputs and Outputs',
        'Processing and Storage',
        'Sequencing',
        'Sprites',
        'Events',
        'Loops',
        'Creative Project Development',
      ],
      project: {
        name: 'Rhythmic Dance Party Project',
        description:
          'Students will create a fun animated dance performance where sprites move and interact based on programmed rules and sequences.',
      },
      closing: [
        'We look forward to helping your child build a strong foundation in technology and creativity.',
        'Best Regards,',
        'RYD Learning Team',
      ],
    },
    {
      level: 'Level 1',
      track: 'Advanced',
      title: 'Introduction to Block-Based Programming',
      subject: 'Welcome to Level 1 – Introduction to Block-Based Programming',
      intro: [
        'Dear Parent,',
        'We are excited to welcome your child to Level 1: Introduction to Block-Based Programming in our upcoming cohort.',
        'At this level, students will begin their journey into event-driven programming using App Lab. Through interactive coding activities and app-building projects, they will learn how to create programs that respond to user actions using events and event handlers. Students will explore key programming concepts such as user input, outputs, variables, buttons, screens, conditionals, functions, and sequencing while strengthening their problem-solving, logical thinking, and computational skills.',
        'Throughout the cohort, students will participate in hands-on activities designed to build confidence and creativity in coding.',
      ],
      learningObjectives: [
        'Introduction to App Lab',
        'Buttons and Events',
        'Multi-Screen Apps',
        'Variables',
        'Building an App - Clicker Game',
        'If Statements',
      ],
      project: {
        name: 'App Explorer: A Multi-Screen Experience',
        description:
          'Students design and develop an interactive multi-screen app that responds to user input using buttons, variables, and conditional logic.',
      },
      closing: [
        'We look forward to helping your child build a strong foundation in technology and creativity.',
        'Best Regards,',
        'RYD Learning Team',
      ],
    },
    {
      level: 'Level 2',
      track: 'Basic',
      title: 'Advanced Block-Based Programming',
      subject: 'Welcome to Level 2 – Advanced Block-Based Programming',
      intro: [
        'Dear Parent,',
        'We are pleased to welcome your child to Level 2: Advanced Block-Based Programming.',
        'This cohort builds on foundational coding skills and introduces students to more advanced programming concepts. Students will learn how to use conditionals, loops, functions, and variables to create more interactive animations and games.',
        'Through engaging projects and practical lessons, students will strengthen their creativity, problem-solving abilities, and logical thinking.',
      ],
      learningObjectives: [
        'Conditionals (If-Else Statements)',
        'While Loops',
        'For Loops',
        'Functions',
        'Variables',
        'Interactive Animations and Games',
      ],
      project: {
        name: 'Interactive Sprite World',
        description:
          'Students will design animations and games where characters and objects interact dynamically based on rules they create.',
      },
      closing: [
        'We are excited to continue supporting your child’s coding journey.',
        'Best Regards,',
        'RYD Learning Team',
      ],
    },
    {
      level: 'Level 2',
      track: 'Advanced',
      title: 'Advanced Block-Based Programming',
      subject: 'Welcome to Level 2 – Advanced Block-Based Programming',
      intro: [
        'Dear Parent,',
        'We are pleased to welcome your child to Level 2: Advanced Block-Based Programming.',
        'In this level, students will continue developing their event-driven programming skills using App Lab by creating more interactive and dynamic applications. They will learn how to use conditional statements, functions, and multiple event handlers to control program behavior and improve user interaction. Students will also explore debugging techniques, app design strategies, and problem-solving methods while building confidence in designing functional, user-friendly applications.',
      ],
      learningObjectives: [
        'User Input and Strings',
        'Boolean Expressions and If Statements',
        'If-Else-If Conditional Logic',
        'Building an App: Color Sleuth',
      ],
      project: {
        name: 'Smart App Challenge: Logic in Action',
        description:
          'Students build an interactive app that utilizes user input, conditional logic, and advanced decision-making to enhance user experience.',
      },
      closing: [
        'We are excited to continue supporting your child’s coding journey.',
        'Best Regards',
        'RYD Learning Team',
      ],
    },
    {
      level: 'Level 3',
      track: 'Standard',
      title: 'Introduction to Python Programming',
      subject: 'Welcome to Level 3 – Introduction to Python Programming',
      intro: [
        'Dear Parent,',
        'We are excited to welcome your child to Level 3: Introduction to Python Programming.',
        'In this cohort, students will transition from block-based coding to text-based programming using Python, one of the world’s most popular programming languages.',
        'Students will learn Python syntax, variables, loops, and functions while building creative coding projects using Python libraries and modules.',
      ],
      learningObjectives: [
        'Python Syntax and Sequences (Pygame)',
        'Arguments and Properties',
        'While Loops',
        'Variables',
        'Creative Coding Techniques',
      ],
      project: {
        name: 'Dragon Art with Python',
        description: 'Students will use Pygame to create digital artworks and explore additional creative coding designs.',
      },
      closing: [
        'We look forward to helping your child develop valuable programming and creative thinking skills.',
        'Best Regards,',
        'RYD Learning Team',
      ],
    },
    {
      level: 'Level 4',
      track: 'Standard',
      title: 'Engineering Design Process with Python',
      subject: 'Welcome to Level 4 – Engineering Design Process with Python',
      intro: [
        'Dear Parent,',
        'We are delighted to welcome your child to Level 4: Engineering Design Process with Python.',
        'This cohort encourages students to apply their Python knowledge in creating an interactive text-based adventure game.',
        'Students will also practice planning, testing, debugging, and refining their work using the Engineering Design Process.',
      ],
      learningObjectives: [
        'Coordinate Systems',
        'Conditionals (if & else)',
        'Functions and Events',
        'Game Design and Storytelling',
        'Collaboration and Project Refinement',
        'Testing and Debugging',
      ],
      project: {
        name: 'Python Quest: The Text Adventure',
        description:
          'Students will design and build a fully interactive adventure game where players make decisions and experience different outcomes.',
      },
      closing: [
        'We are excited to see the creativity and innovation your child will bring to this cohort.',
        'Best Regards,',
        'RYD Learning Team',
      ],
    },
    {
      level: 'Level 5',
      track: 'Standard',
      title: 'Website Development with HTML',
      subject: 'Welcome to Level 5 – Website Development with HTML',
      intro: [
        'Dear Parent,',
        'We are excited to welcome your child to Level 5: Website Development with HTML.',
        'In this cohort, students will begin learning the foundations of website development using HTML.',
      ],
      learningObjectives: [
        'Introduction to HTML',
        'Responsive Web Design',
        'HTML Elements',
        'Images and Lists',
        'HTML Forms',
        'Footer Design',
        'Project-Based Website Development',
      ],
      project: {
        name: 'Imagination Photo App',
        description: 'Students will design and develop a creative gallery webpage using HTML.',
      },
      closing: [
        'We look forward to helping your child explore the exciting world of web development.',
        'Best Regards,',
        'RYD Learning Team',
      ],
    },
    {
      level: 'Level 6',
      track: 'Standard',
      title: 'Styling the Web with CSS',
      subject: 'Welcome to Level 6 – Styling the Web with CSS',
      intro: [
        'Dear Parent,',
        'We are pleased to welcome your child to Level 6: Styling the Web with CSS.',
        'This cohort focuses on teaching students how to style and enhance websites using CSS.',
      ],
      learningObjectives: [
        'Introduction to CSS',
        'CSS Backgrounds',
        'Padding and Spacing',
        'Font Styling',
        'Layout Design',
        'Footer Styling',
        'Creative Web Design',
      ],
      project: {
        name: 'Webpage Makeover: CSS Edition',
        description: 'Students will redesign a basic HTML webpage into a visually engaging website using CSS.',
      },
      closing: [
        'We are excited to see the amazing designs your child will create.',
        'Best Regards',
        'RYD Learning Team',
      ],
    },
    {
      level: 'Level 7',
      track: 'Standard',
      title: 'Website Development with WordPress',
      subject: 'Welcome to Level 7 – Website Development with WordPress',
      intro: [
        'Dear Parent,',
        'We are excited to welcome your child to Level 7: Website Development with WordPress.',
        'In this cohort, students will learn how to build and customize websites using WordPress.',
      ],
      learningObjectives: [
        'Introduction to WordPress',
        'No-Code Website Tools',
        'Website Structure and Setup',
        'Themes and Page Builders',
        'Website Customization',
        'SEO and Marketing Tools',
        'E-Commerce and Analytics',
      ],
      project: {
        name: 'WordPress Website Makeover',
        description: 'Students will transform a simple webpage into a fully customized WordPress website.',
      },
      closing: [
        'We look forward to helping your child build modern digital skills for the future.',
        'Best Regards',
        'RYD Learning Team',
      ],
    },
    {
      level: 'Level 8',
      track: 'Standard',
      title: 'Exploring AI – Creativity & Innovation',
      subject: 'Welcome to Level 8 – Exploring AI: Creativity & Innovation',
      intro: [
        'Dear Parent,',
        'We are delighted to welcome your child to Level 8: Exploring AI – Creativity & Innovation.',
        'This cohort introduces students to the exciting world of Artificial Intelligence.',
      ],
      learningObjectives: [
        'Introduction to Artificial Intelligence',
        'Safety and Privacy',
        'The importance of Data',
        'Ethics and Bias',
        'Impact of AI',
        'Training classifiers',
      ],
      project: {
        name: 'AI Imagination Lab',
        description: 'Students will use AI tools to create creative projects and train simple AI models.',
      },
      closing: [
        'We are excited to help your child explore the future of technology and innovation.',
        'Best Regards',
        'RYD Learning Team',
      ],
    },
    {
      level: 'Level 9',
      track: 'Standard',
      title: 'Game Development – Designing Interactive Worlds',
      subject: 'Welcome to Level 9 – Game Development: Designing Interactive Worlds',
      intro: [
        'Dear Parent,',
        'We are excited to welcome your child to Level 9: Game Development.',
      ],
      learningObjectives: [
        'Spawnables and Game Mechanics',
        'Game Goals and Challenges',
        'Spawnable Properties',
        'Game Statistics',
        'Engineering Design Process',
        'Level Design and Development',
      ],
      project: {
        name: 'Level Up: Build Your Own Game World',
        description: 'Students design and develop their own interactive game level.',
      },
      closing: [
        'We look forward to seeing the exciting worlds your child will create.',
        'Best Regards',
        'RYD Learning Team',
      ],
    },
    {
      level: 'Level 10',
      track: 'Standard',
      title: 'Intermediate Game Development',
      subject: 'Welcome to Level 10 – Intermediate Game Development',
      intro: [
        'Dear Parent,',
        'We are pleased to welcome your child to Level 10: Intermediate Game Development.',
      ],
      learningObjectives: [
        'Events and Game Interactions',
        'Random Numbers in Games',
        'Collisions and Collections',
        'Key Inputs and Controls',
        'Engineering Design Process',
        'Game Testing and Refinement',
      ],
      project: {
        name: 'Interactive Challenge: Collision Quest',
        description: 'Students build an interactive game using collisions and events.',
      },
      closing: [
        'We are excited to support your child as they continue developing game design skills.',
        'Best Regards',
        'RYD Learning Team',
      ],
    },
    {
      level: 'Level 11',
      track: 'Standard',
      title: 'Python Programming – Build Your Own Calculator',
      subject: 'Welcome to Level 11 – Python Programming: Build Your Own Calculator',
      intro: [
        'Dear Parent,',
        'We are delighted to welcome your child to Level 11: Python Programming.',
      ],
      learningObjectives: [
        'Introduction to Python',
        'Variables and Data Types',
        'Operators and Expressions',
        'Input and Output',
        'Conditional Statements',
        'Loops and Functions',
        'Error Handling',
      ],
      project: {
        name: 'Python SmartCalculator',
        description: 'Students build their own functional calculator app using Python.',
      },
      closing: [
        'We look forward to helping your child strengthen coding confidence.',
        'Best Regards',
        'RYD Learning Team',
      ],
    },
    {
      level: 'Level 12',
      track: 'Standard',
      title: 'Web Design Fundamentals – Build Your Personal Portfolio',
      subject: 'Welcome to Level 12 – Web Design Fundamentals',
      intro: [
        'Dear Parent,',
        'We are excited to welcome your child to Level 12: Web Design Fundamentals.',
      ],
      learningObjectives: [
        'Introduction to the Web',
        'HTML Basics',
        'CSS Styling and Layout',
        'Box Model and Spacing',
        'Flexbox and Grid Layouts',
        'Navigation Menus',
        'Forms and Input Fields',
        'Responsive Design',
        'Personal Portfolio Development',
      ],
      project: {
        name: 'My Web Portfolio',
        description: 'Students design and build a personal portfolio website.',
      },
      closing: [
        'We are excited to support your child in developing digital skills.',
        'Best Regards',
        'RYD Learning Team',
      ],
    },
  ] as CurriculumEntry[],
}
